import { isTileStandard } from "~/Configurators/common/tileUtils";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { isNonNull, toMapFn } from "~/utils/reducers";
import { ConfiguratorType } from "~/Configurators/ConfiguratorType";
import * as MetricCstics from "~/Configurators/Shaft/metric/metricCsticConstants";
import { axialThread, chamfer, radialThread } from "~/Configurators/Shaft/metric/metricCsticConstants";
import type { SummaryData, SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import {
  isChamferManualWidth,
  resolveMachiningLabelString,
  resolveShaftToleranceMinmax,
  showAxialThreadSection,
  showChamferedSection,
  showRadialThreadSection,
} from "~/Configurators/Shaft/shaftConfiguratorHelper";
import type { LinearCstic } from "~/Configurators/Linear/models";
import type { WithTranslation } from "react-i18next";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { INCH_UNIT, METRIC_UNIT } from "~/Configurators/Shaft/common/commonShaftConstants";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { NumberFormatter } from "~/utils/NumberFormatter";
import { removeMultipleTags } from "~/utils/domUtils";

interface Step3SummaryData {
  left?: SummaryData & {
    chamfer?: ChamferSummaryData;
    axialThread?: AxialThreadSummaryData;
  };
  middle?: SummaryData & {
    radialThread?: RadialThreadSummaryData;
  };
  right?: SummaryData & {
    chamfer?: ChamferSummaryData;
    axialThread?: AxialThreadSummaryData;
  };
}

interface ChamferSummaryData {
  angle: SummaryData;
  width: SummaryData;
}
interface RadialThreadSummaryData {
  size: SummaryData;
  threadSpacing: SummaryData; // inch and metric
  aL: SummaryData; // inch and metric
  aR: SummaryData; // inch and metric
  threadHoleAmount: SummaryData;
  threadDepth: SummaryData;
}
interface AxialThreadSummaryData {
  size: SummaryData;
  threadDepth: SummaryData;
}

export function getStep1SummaryData(summaryProvider: SummaryDataProvider) {
  const t = summaryProvider.t;

  const substeps = [
    SubstepType.Series,
    SubstepType.Diameter,
    SubstepType.Material,
    SubstepType.Coating,
    SubstepType.Tolerance,
  ];

  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  if (data[SubstepType.Diameter]) {
    data[SubstepType.Diameter].name = t("configurator.shaft.shaftDiameter.title.default");

    let unit;
    if (summaryProvider.getConfiguratorType() === ConfiguratorType.MetricShaft) {
      unit = METRIC_UNIT;
    } else {
      unit = INCH_UNIT;
    }
    data[SubstepType.Diameter].value += unit;
    if (summaryProvider.getConfiguratorType() === ConfiguratorType.MetricShaft) {
      data[SubstepType.Diameter].extra = removeMultipleTags(
        t(summaryProvider.substeps[SubstepType.Diameter].footerInfo[0]),
        "p",
        "span",
      )!;
      data[SubstepType.Diameter].extra = t(summaryProvider.substeps[SubstepType.Diameter].footerInfo[0]);
    }
  }

  const seriesStep = summaryProvider.substeps[SubstepType.Series];
  const selectedTile = seriesStep.tiles[seriesStep.selectedTile!];
  if (selectedTile) {
    data[SubstepType.Series].value = `${t(selectedTile.name)} ${t(selectedTile.description)}`;
  }

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] {
  const t = summaryProvider.t;
  const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

  const data: SummaryData[] = [];

  // Substep 1
  const length = cstics[MetricCstics.SHAFT_LENGTH]?.value;
  if (length) {
    data.push({
      name: t("configurator.shaft.shaftLength.title.default"),
      isStandard: false,
      value: NumberFormatter.getDefaultFormatter().format(+length) + METRIC_UNIT,
    });
  }

  // Substep 2
  const toleranceSubstep = summaryProvider.substeps[SubstepType.ShaftTolerance];
  const selectedTile =
    toleranceSubstep.selectedTile !== null ? toleranceSubstep.tiles[toleranceSubstep.selectedTile] : null;
  if (selectedTile) {
    const minmaxVal = resolveShaftToleranceMinmax(
      ConfiguratorType.MetricShaft,
      Object.values(selectedTile.cstics)[0],
      cstics,
      METRIC_UNIT,
    );
    const value = t(`configurator.shaft.step.2.substep.2.tile.${toleranceSubstep.selectedTile! + 1}.title`);
    data.push({
      name: t("configurator.shaft.shaftLengthTolerance.title.default"),
      isStandard: isTileStandard(selectedTile, summaryProvider.kmats),
      value: [minmaxVal, value].join(" "),
    });
  }

  // Substep 3

  //// Left
  const leftSummary = getMachiningLeft(cstics, t);
  if (leftSummary) {
    leftSummary.value = [leftSummary.value, leftSummary.extra! as string].join(" ");
    delete leftSummary.extra;
    data.push(leftSummary);
  }

  //// Middle
  const middleSummary = getMachiningMiddle(cstics, t);
  if (middleSummary) {
    middleSummary.value = [middleSummary.value, middleSummary.extra! as string].join(" ");
    delete middleSummary.extra;
    data.push(middleSummary);
  }

  //// Right
  const rightSummary = getMachiningRight(cstics, t);
  if (rightSummary) {
    rightSummary.value = [rightSummary.value, rightSummary.extra! as string].join(" ");
    delete rightSummary.extra;
    data.push(rightSummary);
  }

  return data;
}

export function getStep3SummaryData(summaryProvider: SummaryDataProvider): Step3SummaryData {
  const t = summaryProvider.t;
  const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

  const data: Step3SummaryData = {};
  let configured;

  // Left
  const left: Step3SummaryData["left"] = getMachiningLeft(cstics, t)!;
  const leftCstic = cstics[MetricCstics.SHAFT_MACHINING_LEFT];
  configured = false;

  if (showChamferedSection(leftCstic.value)) {
    left.chamfer = resolveChamferSection(cstics, t, chamfer.left);
    configured = true;
  }

  if (showAxialThreadSection(leftCstic.value)) {
    left.axialThread = resolveAxialThreadSection(cstics, t, axialThread.left);
    configured = true;
  }

  if (configured) {
    data.left = left;
  }

  // Middle
  const middle: Step3SummaryData["middle"] = getMachiningMiddle(cstics, t)!;
  const middleCstic = cstics[MetricCstics.SHAFT_MACHINING_MIDDLE];
  configured = false;

  if (showRadialThreadSection(middleCstic.value)) {
    middle.radialThread = resolveRadialThreadSection(cstics, t, radialThread);
    configured = true;
  }

  if (configured) {
    data.middle = middle;
  }

  // Right
  const right: Step3SummaryData["right"] = getMachiningRight(cstics, t)!;
  const rightCstic = cstics[MetricCstics.SHAFT_MACHINING_RIGHT];
  configured = false;

  if (showChamferedSection(rightCstic.value)) {
    right.chamfer = resolveChamferSection(cstics, t, chamfer.right);
    configured = true;
  }

  if (showAxialThreadSection(rightCstic.value)) {
    right.axialThread = resolveAxialThreadSection(cstics, t, axialThread.right);
    configured = true;
  }

  if (configured) {
    data.right = right;
  }

  return data;
}

function resolveChamferSection(
  cstics: Dict<LinearCstic>,
  t: WithTranslation["t"],
  config: (typeof chamfer)["left"],
): ChamferSummaryData {
  const angle = cstics[config.angle]?.value ?? "";
  const isAngleStandard = cstics[config.angle]?.values.find((v) => v.value === angle)?.standard ?? false;
  let width;
  if (isChamferManualWidth(angle)) {
    const val = formatCsticValueAsNumber(cstics[config.width.sf]?.value, 2);
    width = val + MetricCstics.UNIT;
  } else {
    width = cstics[config.width.nf]?.value + MetricCstics.UNIT;
  }
  const angleNumber = angle?.substring(2);
  const angleLabel = t(`configurator.shaft.angle.${angleNumber}`);
  return {
    angle: { name: t("configurator.shaft.chamferAngle.title"), value: angleLabel, isStandard: isAngleStandard },
    width: { name: t("configurator.shaft.chamferWidth.title.default"), value: width ?? "" },
  };
}

function resolveAxialThreadSection(
  cstics: Dict<LinearCstic>,
  t: WithTranslation["t"],
  config: (typeof axialThread)["left"],
) {
  const data: Partial<AxialThreadSummaryData> = {};

  const size = cstics[config.size]?.value ?? "";
  const depth = formatCsticValueAsNumber(cstics[config.depth]?.value, 2) + METRIC_UNIT;
  data.size = {
    name: t("configurator.shaft.step.3.details.axialInternalThread.title"),
    value: size,
  };
  data.threadDepth = { name: t("configurator.shaft.axialThreadDepth.title.default"), value: depth };

  return data as AxialThreadSummaryData;
}

function resolveRadialThreadSection(cstics: Dict<LinearCstic>, t: WithTranslation["t"], config: typeof radialThread) {
  const data: Partial<RadialThreadSummaryData> = {};

  const size = cstics[config.size]?.value ?? "";
  data.size = { name: "", value: size };

  const threadSpacingMode = cstics[config.threadSpacing.mode]?.value ?? "";
  let threadSpacingValue;
  if (threadSpacingMode === config.threadSpacing.modeRecommended) {
    threadSpacingValue = cstics[config.threadSpacing.recommended]?.value;
  } else {
    threadSpacingValue = cstics[config.threadSpacing.individual.value]?.value;
  }
  data.threadSpacing = {
    name: t("configurator.shaft.radialThreadSpacing.title.default"),
    value: formatCsticValueAsNumber(threadSpacingValue, 2) + MetricCstics.UNIT,
  };

  const aL = cstics[config.aL.value]?.value ?? "";
  data.aL = {
    name: t("configurator.shaft.aL.title"),
    value: formatNumberValue(aL, MetricCstics.UNIT),
  };

  const aR = cstics[config.aR.value]?.value ?? "";
  data.aR = {
    name: t("configurator.shaft.aR.title"),
    value: formatNumberValue(aR, MetricCstics.UNIT),
  };

  const threadHoleAmount = cstics[config.threadHoleAmount]?.value ?? "";
  data.threadHoleAmount = {
    name: t("configurator.shaft.step.2.substep.3.extraFields.holeQuantity.title"),
    value: (+threadHoleAmount).toFixed(0),
  };

  const threadDepth = cstics[config.threadDepth]?.value;
  data.threadDepth = {
    name: t("configurator.shaft.radialThreadDepth.title.default"),
    value: formatCsticValueAsNumber(threadDepth, 2) + METRIC_UNIT,
  };

  return data as RadialThreadSummaryData;
}

function formatNumberValue(value: string | undefined, unit?: string) {
  let val = formatCsticValueAsNumber(value, 2);
  if (unit) {
    val += unit;
  }
  return val;
}

function getMachiningLeft(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const leftCstic = cstics[MetricCstics.SHAFT_MACHINING_LEFT];
  if (leftCstic?.value) {
    const selectedValue = leftCstic.values.find((v) => v.value === leftCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.left"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.MetricShaft, t, selectedValue.value),
    };
  }
  return null;
}

function getMachiningMiddle(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const middleCstic = cstics[MetricCstics.SHAFT_MACHINING_MIDDLE];
  if (middleCstic?.value) {
    const selectedValue = middleCstic.values.find((v) => v.value === middleCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.middle"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.MetricShaft, t, selectedValue.value),
    };
  }
  return null;
}

function getMachiningRight(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const rightCstic = cstics[MetricCstics.SHAFT_MACHINING_RIGHT];
  if (rightCstic?.value) {
    const selectedValue = rightCstic.values.find((v) => v.value === rightCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.right"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.MetricShaft, t, selectedValue.value),
    };
  }
  return null;
}
